import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, ExternalLink, Facebook, X, ChevronLeft, ChevronRight } from "lucide-react";

const GALLERY_IMAGES = [
  {
    src: "https://customer-assets.emergentagent.com/job_803c845e-a294-4096-bf98-6bbaa1c9a61f/artifacts/zao0ff4g_IMG_3057.jpeg",
    alt: "Toyota Chaser JZX100 slammed on BBS wheels outside 101 Norman St shop",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_803c845e-a294-4096-bf98-6bbaa1c9a61f/artifacts/n8e2gggp_IMG_3063.jpeg",
    alt: "Honda Integra DC2 before respray - faded paint in workshop",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_803c845e-a294-4096-bf98-6bbaa1c9a61f/artifacts/bq8b9rg3_IMG_3064.jpeg",
    alt: "Honda Integra DC2 after respray - deep red metallic finish",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_803c845e-a294-4096-bf98-6bbaa1c9a61f/artifacts/9li8bvki_IMG_3050.jpeg",
    alt: "Black Toyota Soarer rear view with ITSAH plates",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_803c845e-a294-4096-bf98-6bbaa1c9a61f/artifacts/ms06rzks_IMG_3055.jpeg",
    alt: "VW Scirocco in midnight purple V2 respray",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_803c845e-a294-4096-bf98-6bbaa1c9a61f/artifacts/j8zvic7o_IMG_3058.jpeg",
    alt: "Blue Mazda RX-7 FD rear view",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_803c845e-a294-4096-bf98-6bbaa1c9a61f/artifacts/4b7b87ot_IMG_3056.jpeg",
    alt: "Black Nissan Silvia S14 front view at 101 Norman St",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_803c845e-a294-4096-bf98-6bbaa1c9a61f/artifacts/dg72p1q7_IMG_3054.jpeg",
    alt: "Custom iridescent respray on ute",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_803c845e-a294-4096-bf98-6bbaa1c9a61f/artifacts/opd8h1qx_IMG_3051.jpeg",
    alt: "Blue Holden Commodore VE at 101 Norman St shop",
  },
];

const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 backdrop-blur-sm"
      onClick={onClose}
      data-testid="gallery-lightbox"
    >
      <button
        onClick={onClose}
        data-testid="lightbox-close-btn"
        className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center text-white/70 hover:text-white transition-colors"
      >
        <X size={24} />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        data-testid="lightbox-prev-btn"
        className="absolute left-2 md:left-6 z-10 w-10 h-10 flex items-center justify-center text-white/50 hover:text-white transition-colors"
      >
        <ChevronLeft size={28} />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        data-testid="lightbox-next-btn"
        className="absolute right-2 md:right-6 z-10 w-10 h-10 flex items-center justify-center text-white/50 hover:text-white transition-colors"
      >
        <ChevronRight size={28} />
      </button>

      <img
        src={images[index].src}
        alt={images[index].alt}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[90vw] max-h-[85vh] object-contain select-none"
        data-testid="lightbox-image"
      />

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-zinc-500 text-xs tracking-[0.2em] uppercase font-body">
        {index + 1} / {images.length}
      </div>
    </motion.div>
  );
};

export const GallerySection = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const goPrev = useCallback(() => {
    setLightboxIndex((prev) => (prev === 0 ? GALLERY_IMAGES.length - 1 : prev - 1));
  }, []);
  const goNext = useCallback(() => {
    setLightboxIndex((prev) => (prev === GALLERY_IMAGES.length - 1 ? 0 : prev + 1));
  }, []);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 md:py-32 bg-[#050505]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-[1px] bg-[#3b82f6]" />
              <span className="text-sm tracking-[0.3em] uppercase text-zinc-500 font-body font-medium">Our Work</span>
            </div>
            <h2 data-testid="gallery-heading" className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white leading-[0.95]">
              Gallery
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href="https://www.instagram.com/velocity.paintpanel/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-paint-link"
              className="flex items-center gap-2 border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 px-4 py-2.5 text-sm tracking-[0.1em] uppercase font-body transition-colors no-underline"
            >
              <Instagram size={16} />
              <span className="hidden sm:inline">@velocity.paintpanel</span>
              <span className="sm:hidden">Paint</span>
            </a>
            <a
              href="https://www.instagram.com/velocitycoatingsau/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-coatings-link"
              className="flex items-center gap-2 border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 px-4 py-2.5 text-sm tracking-[0.1em] uppercase font-body transition-colors no-underline"
            >
              <Instagram size={16} />
              <span className="hidden sm:inline">@velocitycoatingsau</span>
              <span className="sm:hidden">Coatings</span>
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61587210241709"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="facebook-link"
              className="flex items-center gap-2 border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 px-4 py-2.5 text-sm tracking-[0.1em] uppercase font-body transition-colors no-underline"
            >
              <Facebook size={16} />
              Velocity Paint and Panel
            </a>
          </div>
        </motion.div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {/* Hero: Toyota Chaser JZX100 - 2x2 top left */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            data-testid="gallery-image-0"
            className="col-span-2 row-span-2 aspect-square overflow-hidden cursor-pointer group"
            onClick={() => openLightbox(0)}
          >
            <img src={GALLERY_IMAGES[0].src} alt={GALLERY_IMAGES[0].alt}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              style={{ imageRendering: 'auto', WebkitBackfaceVisibility: 'hidden', backfaceVisibility: 'hidden' }}
              loading="lazy" />
          </motion.div>

          {/* Integra Before & After - side by side in top right */}
          {GALLERY_IMAGES.slice(1, 3).map((img, i) => (
            <motion.div key={i + 1}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i + 1) * 0.08 }}
              data-testid={`gallery-image-${i + 1}`}
              className="aspect-square overflow-hidden cursor-pointer group relative"
              onClick={() => openLightbox(i + 1)}
            >
              <img src={img.src} alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                style={{ imageRendering: 'auto', WebkitBackfaceVisibility: 'hidden', backfaceVisibility: 'hidden' }}
                loading="lazy" />
              <div className="absolute top-2 left-2 z-10">
                <span className="bg-black/70 backdrop-blur-sm text-white text-[10px] tracking-[0.2em] uppercase font-body px-2 py-1">
                  {i === 0 ? "Before" : "After"}
                </span>
              </div>
            </motion.div>
          ))}

          {/* Row 2 right side: Soarer + Scirocco */}
          {GALLERY_IMAGES.slice(3, 5).map((img, i) => (
            <motion.div key={i + 3}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i + 3) * 0.08 }}
              data-testid={`gallery-image-${i + 3}`}
              className="aspect-square overflow-hidden cursor-pointer group"
              onClick={() => openLightbox(i + 3)}
            >
              <img src={img.src} alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                style={{ imageRendering: 'auto', WebkitBackfaceVisibility: 'hidden', backfaceVisibility: 'hidden' }}
                loading="lazy" />
            </motion.div>
          ))}

          {/* Row 3: RX-7, S14, Patrol Ute, Commodore */}
          {GALLERY_IMAGES.slice(5).map((img, i) => (
            <motion.div key={i + 5}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i + 5) * 0.08 }}
              data-testid={`gallery-image-${i + 5}`}
              className="aspect-square overflow-hidden cursor-pointer group"
              onClick={() => openLightbox(i + 5)}
            >
              <img src={img.src} alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                style={{ imageRendering: 'auto', WebkitBackfaceVisibility: 'hidden', backfaceVisibility: 'hidden' }}
                loading="lazy" />
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-10 flex justify-end"
        >
          <a
            href="https://www.instagram.com/velocity.paintpanel/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gallery-see-more"
            className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm tracking-[0.15em] uppercase font-body transition-colors no-underline"
          >
            See more on Instagram
            <ExternalLink size={14} />
          </a>
        </motion.div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox
            images={GALLERY_IMAGES}
            index={lightboxIndex}
            onClose={closeLightbox}
            onPrev={goPrev}
            onNext={goNext}
          />
        )}
      </AnimatePresence>
    </section>
  );
};
